import { useApolloClient } from '@apollo/client/react'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { withContextRequestHeader } from '@/auth/contextRequestHeader'
import { useAuth } from '@/auth/useAuth'
import { env } from '@/config/env'
import {
  GetMaterialTemplatesDocument,
  MyMaterialsDocument,
} from '@/graphql/api/generated'

interface CreateOptions {
  /** Required when creating in a network context: the owning community IRI. */
  mainOwnership?: string | null
}

/**
 * Creates materials from template ids via the REST endpoint
 * `POST /materials/createFromTemplates` (there is no GraphQL mutation), then
 * invalidates the relevant Apollo caches. Shared by the "add from templates"
 * page and the member profile-onboarding sub-flow. Returns `true` on success.
 */
export const useCreateMaterialsFromTemplates = () => {
  const { t, i18n } = useTranslation()
  const auth = useAuth()
  const apolloClient = useApolloClient()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const createMaterials = useCallback(
    async (
      templateIds: string[],
      options: CreateOptions = {}
    ): Promise<boolean> => {
      if (templateIds.length === 0) {
        return false
      }
      if (!auth.token) {
        toast.error(t('global:please.login'))
        return false
      }

      setIsSubmitting(true)
      try {
        const headers = withContextRequestHeader({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.token}`,
        })
        if (i18n.language) {
          headers['Accept-Language'] = i18n.language
        }

        const response = await fetch(
          `${env.API_URL}/materials/createFromTemplates`,
          {
            method: 'POST',
            headers,
            body: JSON.stringify({
              templateIds,
              ...(options.mainOwnership
                ? { mainOwnership: options.mainOwnership }
                : {}),
            }),
          }
        )

        if (!response.ok) {
          const errorText = await response.text()
          throw new Error(`HTTP ${response.status}: ${errorText}`)
        }

        const result = await response.json()
        const count = result.materials?.length ?? templateIds.length

        toast.success(t('pooling.addFromTemplates.success', { count }))

        await apolloClient.refetchQueries({
          include: [MyMaterialsDocument, GetMaterialTemplatesDocument],
        })
        apolloClient.cache.evict({ id: 'ROOT_QUERY', fieldName: 'myMaterials' })
        apolloClient.cache.evict({
          id: 'ROOT_QUERY',
          fieldName: 'coOwnedMaterials',
        })
        apolloClient.cache.gc()

        return true
      } catch (error) {
        console.error('Error creating materials from templates:', error)
        toast.error(t('pooling.addFromTemplates.error'))
        return false
      } finally {
        setIsSubmitting(false)
      }
    },
    [auth.token, apolloClient, i18n.language, t]
  )

  return { createMaterials, isSubmitting }
}
